import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { extractErrorMessage } from "@/lib/api-errors";
import {
  SEARCH_OPTION,
  boxPointHint,
  dropOffBadge,
  dropOffLabel,
  dropOffOptions,
  dropOffTypeRu,
  shownDropOff,
} from "./dropOff";
import type { KindPick } from "./useOzonDirectionActions";

/** Тип точки бейджем — только у НЕ обычного кросс-дока (ПВЗ, пункт приёма…). */
function TypeBadge({ text }: { text: string }): React.ReactElement {
  return (
    <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300">
      {text}
    </span>
  );
}

/** Выбор «чем и через что везём» — ПЕРВЫЙ шаг, до разговора с площадкой.
 *
 *  Правило компании (прямые в Москву, остальное кросс-доком через ХОРУГВИНО) осталось умолчанием,
 *  но у площадки под сотню пунктов сдачи, и раньше выбора не было вовсе. После создания заявки
 *  выбор замирает подписью: у неё тип уже не меняют.
 *
 *  ⚠️ ПВЗ в списке `/drop-offs` нет никогда — его ищут по ПОЛНОМУ имени пунктом «Другой пункт /
 *  ПВЗ…». Найденная точка встаёт в список отдельной опцией: иначе `<select>` показал бы первый
 *  пункт списка, то есть соврал бы, куда везём.
 */
export function KindPicker({
  d,
  dropOffs,
  disabled,
  onPick,
}: {
  d: ozon.OzonDirection;
  dropOffs: ozon.OzonDropOff[];
  disabled: boolean;
  onPick: (v: KindPick) => void;
}): React.ReactElement {
  const [searching, setSearching] = useState(false);
  const picked = shownDropOff(d, dropOffs);
  const badge = dropOffBadge(picked?.type ?? null);
  const fixed = d.state === "ordered" || d.state === "ordering";
  if (fixed)
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {d.kind === "crossdock"
          ? `кросс-док${picked ? ` · ${dropOffLabel(picked)}` : ""}`
          : "прямая"}
        {d.kind === "crossdock" && badge && <TypeBadge text={badge} />}
      </span>
    );
  const options = dropOffOptions(d, dropOffs);
  const value = d.drop_off_id ?? dropOffs[0]?.id ?? "";
  const hint =
    d.kind === "crossdock" ? boxPointHint(picked?.type ?? null) : null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <select
        className="h-7 rounded border border-input bg-background px-1 text-xs"
        value={d.kind}
        disabled={disabled}
        title="Прямая поставка — склад назначает площадка. Кросс-док — товар сдаём в пункт сдачи, а едет он в кластер."
        onChange={(e) =>
          onPick({
            id: d.shipment_id,
            kind: e.target.value,
            // у кросс-дока пункт обязателен: не выбран — подставляем привычный компании (он первый)
            dropOff:
              e.target.value === "crossdock"
                ? (d.drop_off_id ?? dropOffs[0]?.id ?? null)
                : null,
          })
        }
      >
        <option value="crossdock">кросс-док</option>
        <option value="direct">прямая</option>
      </select>
      {d.kind === "crossdock" && (
        <Popover open={searching} onOpenChange={setSearching}>
          <PopoverAnchor asChild>
            <select
              className="h-7 max-w-56 rounded border border-input bg-background px-1 text-xs"
              value={value}
              disabled={disabled}
              title="Пункт сдачи: товар сдаётся ЗДЕСЬ, а едет в кластер направления. Окна приёмки берутся тоже на нём."
              onChange={(e) => {
                // «Другой пункт / ПВЗ…» — не выбор, а поиск: значение списка остаётся прежним
                if (e.target.value === SEARCH_OPTION) {
                  setSearching(true);
                  return;
                }
                const id = Number(e.target.value);
                onPick({
                  id: d.shipment_id,
                  kind: "crossdock",
                  dropOff: id,
                  dropOffName: options.find((o) => o.id === id)?.name ?? null,
                });
              }}
            >
              {value === "" && <option value="">— пункт сдачи —</option>}
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value={SEARCH_OPTION}>Другой пункт / ПВЗ…</option>
            </select>
          </PopoverAnchor>
          <PopoverContent align="start" className="w-[28rem] p-2 text-xs">
            <DropOffSearch
              onPick={(p) => {
                setSearching(false);
                onPick({
                  id: d.shipment_id,
                  kind: "crossdock",
                  dropOff: p.id,
                  dropOffName: p.name,
                });
              }}
            />
          </PopoverContent>
        </Popover>
      )}
      {d.kind === "crossdock" && badge && <TypeBadge text={badge} />}
      {hint && (
        <span className="basis-full text-xs text-muted-foreground">{hint}</span>
      )}
    </span>
  );
}

/** Поиск пункта сдачи по имени — ПО КНОПКЕ: каждый запрос идёт в Ozon (`/v1/warehouse/fbo/list`).
 *
 *  ⚠️ ПВЗ площадка находит только по ПОЛНОМУ имени, поэтому пустой ответ — не «ПВЗ нет», а «имя
 *  не полное или точка закрыта»; так и пишем.
 */
export function DropOffSearch({
  onPick,
}: {
  onPick: (p: ozon.OzonDropOffFound) => void;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const search = useMutation({ mutationFn: ozon.searchDropOffs });
  return (
    <form
      className="space-y-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        search.mutate(q.trim());
      }}
    >
      <div className="flex items-center gap-1">
        <input
          value={q}
          autoFocus
          onChange={(e) => setQ(e.target.value)}
          placeholder="полное имя точки"
          className="h-7 min-w-0 flex-1 rounded border border-input bg-background px-2"
        />
        <Button
          type="submit"
          size="sm"
          className="h-7"
          disabled={search.isPending || q.trim() === ""}
        >
          Найти
        </Button>
      </div>
      <div className="text-muted-foreground">
        ПВЗ ищется только по ПОЛНОМУ имени, напр. ЛЮБЕРЦЫ_218
      </div>
      {search.isError && (
        <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          ⚠ {extractErrorMessage(search.error, "Ozon не ответил")}
        </div>
      )}
      {search.isPending && (
        <div className="text-muted-foreground">Спрашиваем Ozon…</div>
      )}
      {search.isSuccess && search.data.length === 0 && (
        <div className="text-amber-700 dark:text-amber-400">
          Не нашлось: имя должно быть полным, или точка закрыта.
        </div>
      )}
      {search.isSuccess && search.data.length > 0 && (
        <div className="max-h-60 space-y-0.5 overflow-y-auto">
          {search.data.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className="block w-full rounded px-2 py-1 text-left hover:bg-accent"
            >
              <span className="font-medium">{p.name}</span>
              {" · "}
              {dropOffTypeRu(p.type)}
              {p.address && (
                <span className="text-muted-foreground"> · {p.address}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
